import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '../api/client';
import { getToken } from 'firebase/messaging';
import { getFirebaseMessaging } from '../lib/firebase';
import { useIsInstalledPWA } from '../utils/pwaDetection';
import { isNativePlatform, getPlatform } from '../lib/runtime';
import analytics from '../lib/analytics';

/**
 * On native, request push permission + register FCM token via the Capacitor
 * plugin. The plugin handles the platform-specific dance (FCM on Android,
 * APNs token exchange on iOS). The token format LOOKS the same to our
 * backend — we just register it with platform='android' / platform='ios'
 * instead of 'web' so notificationService.sendPushNotification dispatches
 * correctly. See packages/api/src/lib/fcm.ts.
 */
interface CapacitorPushPlugin {
  checkPermissions:   () => Promise<{ receive: string }>;
  requestPermissions: () => Promise<{ receive: string }>;
  register:           () => Promise<void> | void;
  // Returns synchronously on Android (a PluginListenerHandle), not a Promise.
  addListener: (
    event: string,
    cb:    (data: { value?: string; error?: string }) => void,
  ) => { remove: () => Promise<void> } | Promise<{ remove: () => Promise<void> }>;
}

async function enableNativePush(): Promise<string> {
  // IMPORTANT: do NOT import from '@capacitor/push-notifications'. That npm
  // wrapper calls registerPlugin() from its OWN bundled copy of
  // @capacitor/core, producing a second plugin proxy whose listener registry
  // is disjoint from window.Capacitor.Plugins.*. Native's notifyListeners
  // dispatches through the global only, so wrapper-registered callbacks
  // never fire (token never arrives, /notifications/subscribe never called).
  // Go straight to the global bridge.
  const PushNotifications = (globalThis as any).Capacitor?.Plugins?.PushNotifications as CapacitorPushPlugin | undefined;
  if (!PushNotifications) throw new Error('Capacitor PushNotifications plugin not available');

  const status = await PushNotifications.checkPermissions();
  let perm = status.receive;
  if (perm === 'prompt' || perm === 'prompt-with-rationale') {
    const requested = await PushNotifications.requestPermissions();
    perm = requested.receive;
  }
  if (perm !== 'granted') throw new Error('Notification permission not granted');

  // addListener is async — the native bridge has to round-trip to attach
  // the JS callback. We MUST await both attachments before calling register(),
  // otherwise the native plugin fires `registration` into an empty listener
  // set and the token is dropped on the floor. Symptom: logcat shows
  // "Notifying listeners for event registration" but JS never sees it.
  let resolveToken!: (t: string) => void;
  let rejectToken!: (e: Error) => void;
  const tokenPromise = new Promise<string>((res, rej) => {
    resolveToken = res;
    rejectToken = rej;
  });
  const timeout = setTimeout(() => rejectToken(new Error('Timed out waiting for push token')), 10_000);

  await PushNotifications.addListener('registration', (data) => {
    clearTimeout(timeout);
    if (data.value) resolveToken(data.value);
    else rejectToken(new Error('Push registration returned no token'));
  });
  await PushNotifications.addListener('registrationError', (data) => {
    clearTimeout(timeout);
    rejectToken(new Error(`Push registration failed: ${data.error ?? 'unknown'}`));
  });

  await PushNotifications.register();
  return tokenPromise;
}

export function usePushNotificationToggle() {
  const isPushNotificationsEnabled = useAuthStore((s) => s.isPushNotificationsEnabled);
  const setPushToken = useAuthStore((s) => s.setPushToken);
  const setIsPushNotificationsEnabled = useAuthStore((s) => s.setIsPushNotificationsEnabled);
  const { isInstalled: isPWAInstalled } = useIsInstalledPWA();
  const native = isNativePlatform();

  // On the PWA the home-screen install gate still applies (Web Push only
  // works in installed contexts on iOS). On native the WebView IS the
  // installed app — gate is always open.
  const isEnabled = isPushNotificationsEnabled && (native || isPWAInstalled);
  const canEnable = native || isPWAInstalled;

  const disableMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete('/notifications/disable');
      setPushToken(null);
      setIsPushNotificationsEnabled(false);
      analytics.trackPushTokenDisabled();
      analytics.trackNotificationSettingsChanged(false);
    },
  });

  const enableMutation = useMutation({
    mutationFn: async () => {
      try {
        let token: string;
        let platform: 'web' | 'android' | 'ios';

        if (native) {
          // Native (Capacitor) path. The plugin handles permission +
          // platform-specific token acquisition (FCM on Android, APNs->FCM
          // on iOS if you use Firebase iOS SDK; raw APNs token otherwise).
          token    = await enableNativePush();
          platform = getPlatform() as 'android' | 'ios';
          analytics.trackPushPermission('granted');
        } else {
          // PWA path — unchanged from the pre-Capacitor world.
          let permission = Notification.permission;
          if (permission === 'default') {
            permission = await Notification.requestPermission();
            analytics.trackPushPermission(permission as 'granted' | 'denied' | 'default');
          }
          if (permission !== 'granted') throw new Error('Notification permission not granted');

          const messaging = await getFirebaseMessaging();
          if (!messaging) throw new Error('Firebase messaging not supported');

          const fcmToken = await getToken(messaging, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY });
          if (!fcmToken) throw new Error('Failed to get FCM token');
          token    = fcmToken;
          platform = 'web';
        }

        await apiClient.post('/notifications/subscribe', { token, platform });

        setPushToken(token);
        setIsPushNotificationsEnabled(true);
        analytics.trackPushTokenRegistered();
        analytics.trackNotificationSettingsChanged(true);
      } catch (err) {
        setPushToken(null);
        setIsPushNotificationsEnabled(false);
        const errorMsg = err instanceof Error ? err.message : String(err);
        console.error('[Push Toggle] Error enabling notifications:', {
          error: errorMsg,
          fullError: err,
          isNative: native,
        });
        throw err;
      }
    },
  });

  return {
    isEnabled,
    canEnable,
    isPWAInstalled,
    disableMutation,
    enableMutation,
  };
}
